import { groupBy, maxBy } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import {
  AnalysisResult,
  AnalysisSet,
  CheckResult,
  ComponentDesign,
  LoadCase,
  PlatePair,
} from '@paragon/contract-library';

import { isPassingNonLoadCapacityCheck } from '../../library/isPassingNonLoadCapacityCheck';
import { SelectableItemEntry } from '../../library/selection/types';
import { RootState } from '..';
import { getJointNameFromPlateName } from './componentForcesSelectors';
import { getActiveAnalysisSet } from './getActiveAnalysisSetSelector';
import { getActiveComponentDesign } from './getActiveComponentDesignSelector';
import { getAnalysisResultsForActiveAnalysisSet } from './getAnalysisResultsForActiveAnalysisSetSelector';
import { getLoadCasesForActiveComponentDesign } from './getLoadCasesForActiveComponentDesignSelector';
import { getSingleSelectedItemEntryOrNull } from './getSelectedItemSelector';

const getAnalysisResultForLoadCase = (loadCase: LoadCase, analysisResults: ReadonlyArray<AnalysisResult>) =>
  analysisResults.find((analysisResult) => analysisResult.loadCaseGuid === loadCase.guid);

const sortByNumberOrUndefinedDescending = (
  list: ReadonlyArray<LoadCaseWithCapacity>,
  selector: (loadCase: LoadCaseWithCapacity) => number | undefined,
) =>
  list.toSorted((a, b) => {
    const aResult = selector(a);
    const bResult = selector(b);
    if (aResult === undefined && bResult === undefined) return a.index - b.index;
    if (aResult === undefined) return 1;
    if (bResult === undefined) return -1;
    return bResult - aResult;
  });

interface MutableLoadCaseWithCapacity extends LoadCase {
  capacity: number | undefined;
  csi: number | undefined;
  jsi: number | undefined;
  sortCsi: number | undefined;
  sortJsi: number | undefined;
}
export type LoadCaseWithCapacity = Readonly<MutableLoadCaseWithCapacity>;

const getJsi = (
  checkResults: readonly CheckResult[] | undefined,
  selectedEntry: SelectableItemEntry | null,
  platePairs: ReadonlyArray<PlatePair>,
) => {
  if (checkResults == null || selectedEntry == null) return undefined;
  const checkGroups = groupBy(checkResults, (check) => check.tpiSection);
  const jsiChecks = checkGroups['8.3'];
  if (jsiChecks === undefined) return undefined;
  let relevantChecks: CheckResult[] = [];
  if (selectedEntry?.type === 'platePair') {
    relevantChecks = jsiChecks.filter((checkResult) =>
      checkResult.objects.find((checkObject) => checkObject.guid === selectedEntry.item.guid),
    );
  } else if (selectedEntry?.type === 'analogJoint') {
    const correspondingPlatePairGuids = platePairs
      .filter((platePair) => getJointNameFromPlateName(platePair.name) === selectedEntry.item.name)
      .map((platePair) => platePair.guid);
    relevantChecks = jsiChecks.filter((checkResult) => {
      for (const correspondingPlatePairGuid of correspondingPlatePairGuids) {
        if (checkResult.objects.find((checkObject) => checkObject.guid === correspondingPlatePairGuid)) return true;
      }
      return false;
    });
  } else {
    return undefined;
  }
  if (relevantChecks.length !== 1) {
    console.warn('Expecting one jsi check');
    return undefined;
  }
  return relevantChecks[0].capacity;
};

const getCsiChecks = (
  csiWebChecks: CheckResult[] | undefined,
  csiChordChecks: CheckResult[] | undefined,
  selectedItemGuid: string,
) => {
  if (csiChordChecks !== undefined) {
    const relevantChordChecks = csiChordChecks.filter((checkResult) =>
      checkResult.objects.find((checkObject) => checkObject.guid === selectedItemGuid),
    );
    if (relevantChordChecks.length > 0) return relevantChordChecks;
  }
  if (csiWebChecks !== undefined) {
    return csiWebChecks.filter((checkResult) =>
      checkResult.objects.find((checkObject) => checkObject.guid === selectedItemGuid),
    );
  }
  return null;
};

const getCsi = (checkResults: readonly CheckResult[] | undefined, selectedEntry: SelectableItemEntry | null) => {
  if (checkResults === undefined || selectedEntry === null) return undefined;
  const checkGroups = groupBy(checkResults, (check) => check.tpiSection);
  const csiWebChecks = checkGroups['7.3.1-2'];
  const csiChordChecks = checkGroups['7.3.4-5'];
  let relevantChecks: CheckResult[] = [];
  if (selectedEntry?.type === 'analogLink' || selectedEntry?.type === 'member') {
    const csiChecks = getCsiChecks(csiWebChecks, csiChordChecks, selectedEntry.item.guid);
    if (csiChecks === null) return undefined;
    relevantChecks = csiChecks;
  } else {
    return undefined;
  }

  if (relevantChecks.length !== 1) {
    console.warn('Expecting one csi check');
    return undefined;
  }
  return relevantChecks[0].capacity;
};

const getSortedLoadCases = (
  loadCasesForActiveComponent: readonly LoadCase[],
  selectedLoadCaseGuid: string | null,
  filterToSelectedLoadCase: boolean,
  analysisResults: readonly AnalysisResult[],
  selectedEntry: SelectableItemEntry | null,
  sortSelectedEntry: SelectableItemEntry | null,
  sortType: string,
  activeComponentDesign: ComponentDesign | null,
  analysisSet: AnalysisSet | null,
) => {
  if (loadCasesForActiveComponent == null) return [];
  const loadCasesWithCapacity = loadCasesForActiveComponent
    .filter((loadCase) => !filterToSelectedLoadCase || loadCase.guid === selectedLoadCaseGuid)
    .map((loadCase): LoadCaseWithCapacity => {
      const analysisResult = getAnalysisResultForLoadCase(loadCase, analysisResults);
      if (analysisResult !== undefined) {
        const { capacity } = maxBy(analysisResult.checkResults, (checkResult) => {
          if (isPassingNonLoadCapacityCheck(checkResult)) return -1;
          return checkResult.capacity;
        })!;
        const { platePairs } = activeComponentDesign!.component;
        const jsi = getJsi(analysisResult.checkResults, selectedEntry, platePairs);
        const csi = getCsi(analysisResult.checkResults, selectedEntry);
        const sortJsi = getJsi(analysisResult.checkResults, sortSelectedEntry, platePairs);
        const sortCsi = getCsi(analysisResult.checkResults, sortSelectedEntry);
        return { ...loadCase, capacity, jsi, csi, sortJsi, sortCsi };
      }
      return {
        ...loadCase,
        capacity: analysisSet?.loadCaseCapacities?.[loadCase.guid],
        jsi: undefined,
        csi: undefined,
        sortJsi: undefined,
        sortCsi: undefined,
      };
    });
  if (sortType === 'index') {
    return loadCasesWithCapacity.toSorted((a, b) => a.index - b.index);
  }
  if (sortType === 'csi') {
    return sortByNumberOrUndefinedDescending(loadCasesWithCapacity, (loadCase) => loadCase.sortCsi);
  }
  if (sortType === 'jsi') {
    return sortByNumberOrUndefinedDescending(loadCasesWithCapacity, (loadCase) => loadCase.sortJsi);
  }
  return sortByNumberOrUndefinedDescending(loadCasesWithCapacity, (loadCase) => loadCase.capacity);
};

export const getLoadCasesForActiveComponentDesignSorted = createSelector(
  getLoadCasesForActiveComponentDesign,
  getAnalysisResultsForActiveAnalysisSet,
  getSingleSelectedItemEntryOrNull,
  (state: RootState) => state.panelData.loadCaseSortSelectedItemEntry,
  (state: RootState) => state.panelData.loadCaseSortType,
  getActiveComponentDesign,
  getActiveAnalysisSet,
  (
    loadCasesForActiveComponent,
    analysisResults,
    selectedEntry,
    sortSelectedEntry,
    sortType,
    activeComponentDesign,
    analysisSet,
  ) =>
    getSortedLoadCases(
      loadCasesForActiveComponent,
      null,
      false,
      analysisResults,
      selectedEntry,
      sortSelectedEntry,
      sortType,
      activeComponentDesign,
      analysisSet,
    ),
);

export const getSelectedLoadCaseInfoForSelectedItem = createSelector(
  getLoadCasesForActiveComponentDesign,
  (state: RootState) => state.selections.selectedCheckResultsLoadCaseGuid,
  getAnalysisResultsForActiveAnalysisSet,
  getSingleSelectedItemEntryOrNull,
  (state: RootState) => state.panelData.loadCaseSortSelectedItemEntry,
  (state: RootState) => state.panelData.loadCaseSortType,
  getActiveComponentDesign,
  getActiveAnalysisSet,
  (
    loadCasesForActiveComponent,
    selectedLoadCaseGuid,
    analysisResults,
    selectedEntry,
    sortSelectedEntry,
    sortType,
    activeComponentDesign,
    analysisSet,
  ): LoadCaseWithCapacity | undefined =>
    getSortedLoadCases(
      loadCasesForActiveComponent,
      selectedLoadCaseGuid,
      true,
      analysisResults,
      selectedEntry,
      sortSelectedEntry,
      sortType,
      activeComponentDesign,
      analysisSet,
    )[0],
);
